import { useState, useEffect, useMemo } from 'react';
import {
  CreditCard,
  Banknote,
  Plus,
  Trash2,
  Loader2,
  DollarSign,
  CheckCircle2,
  AlertCircle,
  Split,
} from 'lucide-react';
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalHeader,
  ResponsiveModalBody,
  ResponsiveModalFooter,
  ResponsiveModalTitle,
  ResponsiveModalDescription,
} from '@/components/ui/responsive-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useCurrency } from '@/hooks/useCurrency';

export type PaymentMethodType = 'cash' | 'card' | 'gift_card' | 'store_credit' | 'other';

export interface PaymentType {
  id: string;
  name: string;
  method: PaymentMethodType;
  is_primary?: boolean | null;
  is_cash_equivalent?: boolean;
}

interface SplitPaymentEntry {
  id: string;
  paymentTypeId: string;
  paymentTypeName: string;
  paymentMethod: PaymentMethodType;
  amount: number; // For cash: what customer gave. For others: exact amount to charge
  isPaid: boolean;
}

interface SplitPaymentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderTotal: number;
  availablePaymentTypes: PaymentType[];
  onProcessPayments: (
    payments: Array<{
      paymentTypeId: string;
      paymentMethod: PaymentMethodType;
      amount: number;
      cashTendered?: number;
    }>
  ) => Promise<void>;
  isProcessing?: boolean;
}

const paymentMethodIcons: Record<PaymentMethodType, typeof CreditCard> = {
  cash: Banknote,
  card: CreditCard,
  gift_card: CreditCard,
  store_credit: DollarSign,
  other: DollarSign,
};

export function SplitPaymentDialog({
  open,
  onOpenChange,
  orderTotal,
  availablePaymentTypes,
  onProcessPayments,
  isProcessing = false,
}: SplitPaymentDialogProps) {
  const { formatCurrency } = useCurrency();
  const [payments, setPayments] = useState<SplitPaymentEntry[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Helper to check if a payment is cash-type
  const isCashPayment = (payment: SplitPaymentEntry): boolean => {
    return payment.paymentMethod === 'cash' ||
      availablePaymentTypes.find(t => t.id === payment.paymentTypeId)?.is_cash_equivalent === true;
  };

  // Calculate applied amounts and change for each payment
  const paymentCalculations = useMemo(() => {
    // First, sum all non-cash payments
    const nonCashTotal = payments
      .filter(p => !isCashPayment(p))
      .reduce((sum, p) => sum + p.amount, 0);

    let remainingAfterNonCash = Math.max(0, orderTotal - nonCashTotal);

    // Calculate applied amount and change for each payment
    const calculations: Record<string, { applied: number; change: number }> = {};

    for (const payment of payments) {
      if (!isCashPayment(payment)) {
        // Non-cash: applied = amount entered
        calculations[payment.id] = { applied: payment.amount, change: 0 };
      } else {
        // Cash: applied = min(amount, remaining), change = amount - applied
        const applied = Math.min(payment.amount, remainingAfterNonCash);
        const change = Math.max(0, payment.amount - applied);
        calculations[payment.id] = { applied, change };
        remainingAfterNonCash = Math.max(0, remainingAfterNonCash - applied);
      }
    }

    return calculations;
  }, [payments, orderTotal, availablePaymentTypes]);

  // Total amount applied to the order
  const totalApplied = useMemo(() => {
    return Object.values(paymentCalculations).reduce((sum, calc) => sum + calc.applied, 0);
  }, [paymentCalculations]);

  // Total change due
  const totalChangeDue = useMemo(() => {
    return Object.values(paymentCalculations).reduce((sum, calc) => sum + calc.change, 0);
  }, [paymentCalculations]);

  const remainingToAssign = orderTotal - totalApplied;
  const isOrderCovered = Math.abs(remainingToAssign) < 0.01;

  // Initialize with default payment types when dialog opens
  useEffect(() => {
    if (open) {
      // Start with two default payments (cash and card if available)
      const cashType = availablePaymentTypes.find(t => t.method === 'cash');
      const cardType = availablePaymentTypes.find(t => t.method === 'card');

      const initialPayments: SplitPaymentEntry[] = [];

      if (cashType) {
        initialPayments.push({
          id: `payment-1`,
          paymentTypeId: cashType.id,
          paymentTypeName: cashType.name,
          paymentMethod: cashType.method,
          amount: 0,
          isPaid: false,
        });
      }

      if (cardType) {
        initialPayments.push({
          id: `payment-2`,
          paymentTypeId: cardType.id,
          paymentTypeName: cardType.name,
          paymentMethod: cardType.method,
          amount: 0,
          isPaid: false,
        });
      }

      // If no cash/card, just add first two available
      if (initialPayments.length === 0 && availablePaymentTypes.length > 0) {
        initialPayments.push({
          id: `payment-1`,
          paymentTypeId: availablePaymentTypes[0].id,
          paymentTypeName: availablePaymentTypes[0].name,
          paymentMethod: availablePaymentTypes[0].method,
          amount: 0,
          isPaid: false,
        });
      }

      setPayments(initialPayments);
      setError(null);
    }
  }, [open, availablePaymentTypes]);

  const addPayment = () => {
    // Find a payment type that's not already used, or use the first one
    const usedIds = new Set(payments.map(p => p.paymentTypeId));
    const availableType = availablePaymentTypes.find(t => !usedIds.has(t.id)) || availablePaymentTypes[0];

    if (!availableType) return;

    setPayments(prev => [
      ...prev,
      {
        id: `payment-${Date.now()}`,
        paymentTypeId: availableType.id,
        paymentTypeName: availableType.name,
        paymentMethod: availableType.method,
        amount: 0,
        isPaid: false,
      },
    ]);
  };

  const removePayment = (id: string) => {
    if (payments.length <= 2) return; // Keep at least 2 payments for split
    setPayments(prev => prev.filter(p => p.id !== id));
  };

  const updatePaymentType = (id: string, paymentTypeId: string) => {
    const paymentType = availablePaymentTypes.find(t => t.id === paymentTypeId);
    if (!paymentType) return;

    setPayments(prev =>
      prev.map(p =>
        p.id === id
          ? {
              ...p,
              paymentTypeId: paymentType.id,
              paymentTypeName: paymentType.name,
              paymentMethod: paymentType.method,
            }
          : p
      )
    );
  };

  const updatePaymentAmount = (id: string, amount: string) => {
    const numAmount = parseFloat(amount) || 0;
    setPayments(prev =>
      prev.map(p => (p.id === id ? { ...p, amount: Math.max(0, numAmount) } : p))
    );
  };

  const autoFillRemaining = (id: string) => {
    if (remainingToAssign <= 0) return;
    setPayments(prev =>
      prev.map(p => (p.id === id ? { ...p, amount: p.amount + remainingToAssign } : p))
    );
  };

  const handleProcessPayments = async () => {
    if (!isOrderCovered) {
      setError(`Total applied (${formatCurrency(totalApplied)}) doesn't match order total (${formatCurrency(orderTotal)})`);
      return;
    }

    const validPayments = payments.filter(p => {
      const calc = paymentCalculations[p.id];
      return calc && calc.applied > 0;
    });

    if (validPayments.length === 0) {
      setError('Please assign amounts to at least one payment method');
      return;
    }

    setError(null);

    try {
      await onProcessPayments(
        validPayments.map(p => {
          const calc = paymentCalculations[p.id];
          const isCash = isCashPayment(p);
          return {
            paymentTypeId: p.paymentTypeId,
            paymentMethod: p.paymentMethod,
            amount: calc.applied, // Send the applied amount, not the tendered amount
            cashTendered: isCash && p.amount > calc.applied ? p.amount : undefined,
          };
        })
      );
      onOpenChange(false);
    } catch (err: any) {
      setError(err.message || 'Failed to process payments');
    }
  };

  const handleClose = () => {
    if (!isProcessing) {
      onOpenChange(false);
    }
  };

  return (
    <ResponsiveModal open={open} onOpenChange={handleClose}>
      <ResponsiveModalContent maxWidth="lg">
        <ResponsiveModalHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Split className="h-5 w-5 text-primary" />
            </div>
            <div>
              <ResponsiveModalTitle>Split Payment</ResponsiveModalTitle>
              <ResponsiveModalDescription>
                Divide the payment across multiple payment methods
              </ResponsiveModalDescription>
            </div>
          </div>
        </ResponsiveModalHeader>

        <ResponsiveModalBody className="space-y-6">
          {/* Order Total Summary */}
          <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
            <div>
              <p className="text-sm text-muted-foreground">Order Total</p>
              <p className="text-2xl font-bold">{formatCurrency(orderTotal)}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">Remaining to Assign</p>
              <p className={cn(
                "text-2xl font-bold",
                isOrderCovered ? "text-green-600" : remainingToAssign > 0 ? "text-amber-600" : "text-red-600"
              )}>
                {isOrderCovered ? (
                  <span className="flex items-center gap-1">
                    <CheckCircle2 className="h-5 w-5" />
                    Balanced
                  </span>
                ) : (
                  formatCurrency(remainingToAssign)
                )}
              </p>
            </div>
          </div>

          {/* Payment Entries */}
          <div className="space-y-4">
            {payments.map((payment, index) => {
              const Icon = paymentMethodIcons[payment.paymentMethod];
              const isCash = isCashPayment(payment);
              const calc = paymentCalculations[payment.id] || { applied: 0, change: 0 };

              return (
                <div
                  key={payment.id}
                  className="p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-start gap-4">
                    {/* Payment Type Selector */}
                    <div className="flex-1 space-y-2">
                      <Label htmlFor={`payment-type-${payment.id}`}>Payment Method {index + 1}</Label>
                      <select
                        id={`payment-type-${payment.id}`}
                        title={`Select payment method ${index + 1}`}
                        className="w-full p-2 rounded-md border bg-background"
                        value={payment.paymentTypeId}
                        onChange={(e) => updatePaymentType(payment.id, e.target.value)}
                        disabled={isProcessing}
                      >
                        {availablePaymentTypes.map(type => (
                          <option key={type.id} value={type.id}>
                            {type.name} ({type.method})
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Amount Input */}
                    <div className="flex-1 space-y-2">
                      <Label>{isCash ? 'Cash Received' : 'Amount'}</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                            $
                          </span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={payment.amount || ''}
                            onChange={(e) => updatePaymentAmount(payment.id, e.target.value)}
                            className="pl-7 text-right font-mono"
                            placeholder="0.00"
                            disabled={isProcessing}
                          />
                        </div>
                        {remainingToAssign > 0 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => autoFillRemaining(payment.id)}
                            disabled={isProcessing}
                            title="Fill remaining amount"
                          >
                            +{formatCurrency(remainingToAssign)}
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Remove Button */}
                    <div className="pt-7">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePayment(payment.id)}
                        disabled={payments.length <= 2 || isProcessing}
                        className="text-muted-foreground hover:text-red-600"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Payment Info */}
                  <div className="mt-3 flex items-center gap-2">
                    <Badge variant="outline" className="gap-1">
                      <Icon className="h-3 w-3" />
                      {payment.paymentTypeName}
                    </Badge>
                    {calc.applied > 0 && (
                      <span className="text-sm font-medium">
                        {formatCurrency(calc.applied)} applied
                      </span>
                    )}
                  </div>

                  {/* Change Due Display - for cash payments with overpayment */}
                  {isCash && calc.change > 0 && (
                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-green-700 dark:text-green-300">
                          Change Due
                        </span>
                        <span className="text-xl font-bold text-green-700 dark:text-green-300">
                          {formatCurrency(calc.change)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add Payment Button */}
          <Button
            type="button"
            variant="outline"
            className="w-full gap-2"
            onClick={addPayment}
            disabled={isProcessing || availablePaymentTypes.length === 0}
          >
            <Plus className="h-4 w-4" />
            Add Payment Method
          </Button>

          {/* Error Display */}
          {error && (
            <div className="p-3 text-sm text-red-600 bg-red-50 dark:bg-red-950/20 rounded-lg flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Summary */}
          <div className="p-4 bg-muted/30 rounded-lg space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Order Total:</span>
              <span className="font-medium">{formatCurrency(orderTotal)}</span>
            </div>
            {payments.filter(p => paymentCalculations[p.id]?.applied > 0).map(payment => {
              const calc = paymentCalculations[payment.id];
              const isCash = isCashPayment(payment);
              return (
                <div key={payment.id}>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{payment.paymentTypeName}:</span>
                    <span className="font-medium">{formatCurrency(calc.applied)}</span>
                  </div>
                  {isCash && calc.change > 0 && (
                    <div className="flex justify-between text-xs text-muted-foreground ml-4">
                      <span>(Received: {formatCurrency(payment.amount)}, Change: {formatCurrency(calc.change)})</span>
                    </div>
                  )}
                </div>
              );
            })}
            <div className="border-t pt-2 mt-2">
              <div className="flex justify-between font-medium">
                <span>Total Applied:</span>
                <span className={cn(
                  isOrderCovered ? "text-green-600" : "text-amber-600"
                )}>
                  {formatCurrency(totalApplied)}
                </span>
              </div>
              {totalChangeDue > 0 && (
                <div className="flex justify-between font-bold text-green-600 mt-1">
                  <span>Total Change Due:</span>
                  <span>{formatCurrency(totalChangeDue)}</span>
                </div>
              )}
            </div>
          </div>
        </ResponsiveModalBody>

        <ResponsiveModalFooter>
          <Button type="button" variant="outline" onClick={handleClose} disabled={isProcessing}>
            Cancel
          </Button>
          <Button
            onClick={handleProcessPayments}
            disabled={isProcessing || !isOrderCovered || payments.filter(p => paymentCalculations[p.id]?.applied > 0).length === 0}
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Process Split Payment
              </>
            )}
          </Button>
        </ResponsiveModalFooter>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}









